import axios from 'axios'
import i18n from '@/i18n/i18nMain'

/**
 * Course copy as a background job the browser polls.
 *
 * The API sits behind Cloudflare's proxy, which answers 524 to any response
 * slower than 100 s, and a copy can take well over a minute. A 524 while the
 * copy carries on server-side makes teachers retry and duplicate content. So
 * the copy endpoints are called with `async: 1`: they validate and authorise
 * as before (a 403 still rejects straight away), answer 202 {job_id}, and run
 * the copy after the response. This polls GET /adaptiveobject/copy/jobs/{id}
 * every 2 s until the job is done or failed.
 *
 * Callers get what the synchronous request gave them, so none of them had to
 * change:
 *  - done   → resolves `{ status, data }` with the sync endpoint's body
 *             (`data.new_object_id`, `data.group_id`, ...).
 *  - failed → rejects `{ status, data }` the way the axios interceptor rejects
 *             a failed request (it rejects with the RESPONSE: callers read
 *             `err.status` and `err.data.message`).
 *  - an API that answers the POST without a job (a build from before this)
 *    → that response, unchanged.
 *
 * A failed poll (network blip, a deploy's 502) does not end the wait: the
 * copy is still running, and giving up is exactly what made teachers copy
 * twice. Only 401/403/404 do, since those will not change. After 15 minutes
 * it gives up with a message that says the copy may still finish.
 */

export const COPY_JOB_POLL_INTERVAL_MS = 2000
export const COPY_JOB_TIMEOUT_MS = 15 * 60 * 1000

export const COPY_JOB_TIMEOUT_MESSAGE = 'The copy is taking longer than expected. It may still finish, so check the class in a few minutes before you copy again.'
export const COPY_JOB_FAILED_MESSAGE = 'The copy failed. Please try again.'

const FINAL_POLL_STATUSES = [401, 403, 404]

function translate(key) {
    try {
        const out = i18n.global ? i18n.global.t(key) : key
        return typeof out === 'string' && out ? out : key
    } catch (err) {
        return key
    }
}

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function failure(job) {
    const status = job.http_status || 500
    const data = (job.result && typeof job.result === 'object' && !Array.isArray(job.result))
        ? { ...job.result }
        : {}
    if (typeof data.message !== 'string' || !data.message) {
        data.message = job.error || translate(COPY_JOB_FAILED_MESSAGE)
    }
    return { status, data }
}

/**
 * POST `url` with `body` + `async: 1`, then wait for the job.
 *
 * @param {string} url  copy endpoint
 * @param {Object} body the request body the synchronous call sent
 * @param {Object} [options] seams for tests
 * @return {Promise<{status: number, data: *}>}
 */
export async function runCopyJob(url, body, {
    http = axios,
    intervalMs = COPY_JOB_POLL_INTERVAL_MS,
    timeoutMs = COPY_JOB_TIMEOUT_MS,
    sleep = wait,
    now = () => Date.now(),
} = {}) {
    const started = await http.post(url, { ...body, async: 1 })

    const jobId = started && started.status === 202 && started.data ? started.data.job_id : null
    if (!jobId) {
        return started
    }

    const deadline = now() + timeoutMs
    while (now() < deadline) {
        await sleep(intervalMs)

        let job
        try {
            job = (await http.get(`/adaptiveobject/copy/jobs/${jobId}`)).data
        } catch (err) {
            if (err && FINAL_POLL_STATUSES.includes(err.status)) {
                throw err
            }
            continue
        }

        if (job && job.status === 'done') {
            return { status: job.http_status || 200, data: job.result }
        }
        if (job && job.status === 'failed') {
            throw failure(job)
        }
    }

    throw { status: 504, data: { message: translate(COPY_JOB_TIMEOUT_MESSAGE) } }
}
